import { Controller } from '@hotwired/stimulus'

/**
 * Client-side CSV export for visible drill-down table data.
 * Scrapes the DOM table — what you see is what you get.
 *
 * Each drill-down dimension gets its own column in the CSV. The badge text
 * (e.g. "Campaign", "Publisher", "Call Buyer", "Revenue", "Daily") determines which column
 * the row's value is placed into. Other dimension columns are left blank.
 *
 * Example output:
 *   Campaign,Publisher,Call Buyer,Total,Revenue
 *   1ee94a75 - Test dev Campaign123,,,118,"$505,298"
 *   ,3333 - Test Test,,34,"$15,391"
 *   ,,No Call Buyer,8,"$58,114"
 *
 * Usage:
 *   <section data-controller="drill-down-table-csv-export">
 *     <button data-action="click->drill-down-table-csv-export#export">CSV Export</button>
 *     <table data-drill-down-table-csv-export-target="table">...</table>
 *   </section>
 */
export default class extends Controller {
    static targets = ['table']

    export() {
        const csv = this.buildCsv(this.tableTarget)
        this.download(csv)
    }

    buildCsv(table) {
        // Combine tbody and tfoot rows into single collection
        const allRows = [...table.querySelectorAll('tbody tr'), ...table.querySelectorAll('tfoot tr')]

        // First pass: collect unique dimension names in order of first appearance
        const dimensionNames = []
        allRows.forEach((tr) => {
            const badge = tr.querySelector('td .badge')
            if (badge) {
                const name = badge.textContent.trim()
                if (!dimensionNames.includes(name)) {
                    dimensionNames.push(name)
                }
            }
        })

        // Collect visible fact column headers (skip the first <th> — it's the dimension column)
        const headerCells = table.querySelectorAll('thead th')
        const factHeaders = []
        const visibleFactIndices = new Set()

        headerCells.forEach((th, index) => {
            if (index === 0 || this.isHidden(th)) return
            factHeaders.push(this.escapeCsvValue(th.textContent.trim()))
            visibleFactIndices.add(index)
        })

        // Build header row: Type + dimension columns + fact columns
        const csvRows = []
        csvRows.push(['Type', ...dimensionNames.map((n) => this.escapeCsvValue(n)), ...factHeaders].join(','))

        // Process all rows (tbody + tfoot)
        allRows.forEach((tr) => {
            const cells = tr.querySelectorAll('td')
            const firstCell = cells[0]
            const firstCellText = firstCell.textContent.trim()

            // Insert empty separator row before "Totals" row
            if (firstCellText === 'Totals') {
                const totalCols = 1 + dimensionNames.length + factHeaders.length
                csvRows.push(','.repeat(totalCols - 1))
            }

            const badge = firstCell.querySelector('.badge')

            if (!badge && firstCellText !== 'Totals') {
                // Non-data row (e.g. "Showing 3 results. Load more...")
                const totalCols = dimensionNames.length + factHeaders.length + 1
                csvRows.push(this.escapeCsvValue(firstCellText) + ','.repeat(totalCols - 1))
                return
            }

            // Totals row (no badge, first cell is "Totals")
            if (!badge && firstCellText === 'Totals') {
                const totalsDimensionCols = ['Totals', ...dimensionNames.map(() => '')]
                const totalsFactCols = this.extractFactColumns(cells, visibleFactIndices)
                csvRows.push([...totalsDimensionCols, ...totalsFactCols].join(','))
                return
            }

            // Regular data row (has badge)
            const badgeText = badge.textContent.trim()
            const valueText = this.getFirstCellValue(firstCell)

            const dimensionCols = [this.escapeCsvValue(badgeText), ...dimensionNames.map((name) => (name === badgeText ? this.escapeCsvValue(valueText) : ''))]
            const factCols = this.extractFactColumns(cells, visibleFactIndices)

            csvRows.push([...dimensionCols, ...factCols].join(','))
        })

        return csvRows.join('\n')
    }

    /**
     * Extract fact column values from cells, skipping the first column and hidden columns
     */
    extractFactColumns(cells, visibleFactIndices) {
        const factCols = []
        cells.forEach((td, index) => {
            if (index === 0) return
            if (visibleFactIndices.has(index)) {
                factCols.push(this.escapeCsvValue(td.textContent.trim()))
            }
        })
        return factCols
    }

    /**
     * Extract only bare text nodes from the first cell, removing all the spans as they contain icons and chevrons
     */
    getFirstCellValue(cell) {
        const clonedCell = cell.cloneNode(true)
        clonedCell.querySelectorAll('span').forEach((el) => el.remove())
        return clonedCell.textContent.trim()
    }

    escapeCsvValue(value) {
        if (/^[=+\-@\t]/.test(value)) {
            value = "'" + value   // leading apostrophe disables formula execution
        }
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            return '"' + value.replace(/"/g, '""') + '"'
        }
        return value
    }

    isHidden(element) {
        // # If we try to export an edited, but not saved template
        return element.classList.contains('d-none')
    }

    download(csv) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const date = new Date().toISOString().slice(0, 10)

        const link = document.createElement('a')
        link.href = url
        link.download = `drill_down_export_${date}.csv`
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }
}
